import { Platform } from 'react-native';

/**
 * What version of this app is installed, and on what.
 *
 * ---------------------------------------------------------------------------
 * WHY THE NATIVE VALUE IS READ THROUGH A TRY/CATCH
 * ---------------------------------------------------------------------------
 * `react-native-device-info` reads the real `versionName` on Android and
 * `CFBundleShortVersionString` on iOS - the number the store shows, which is
 * the only one a person can act on when told to update.
 *
 * But it is a native module, and a native module is absent in three situations
 * that all matter here: under Jest, in a JS-only reload of a build that shipped
 * before the dependency was added, and in any build where the pods were not
 * reinstalled. In every one of those `getVersion()` throws.
 *
 * A throw on the launch path is not acceptable for a feature whose entire job
 * is to decide whether the app may run - so the native read is guarded and
 * falls back to `FALLBACK_VERSION` below.
 *
 * ---------------------------------------------------------------------------
 * KEEP `FALLBACK_VERSION` IN STEP WITH THE NATIVE VERSION
 * ---------------------------------------------------------------------------
 * It is only reached when the native module is missing, and in that case being
 * wrong is safe rather than dangerous: the backend fails open on a version it
 * cannot make sense of, so a stale fallback means somebody is not blocked who
 * might have been. Never the reverse.
 */

/**
 * The version to report when the native module cannot be reached.
 *
 * Bump alongside `android/app/build.gradle`'s `versionName` and the iOS
 * `MARKETING_VERSION` at release time.
 */
export const FALLBACK_VERSION = '3.2';

/** `android` or `ios`, as the platform names itself to our API. */
export const appPlatform = (): 'android' | 'ios' =>
  Platform.OS === 'ios' ? 'ios' : 'android';

let cached: string | null = null;

/**
 * The installed version, e.g. `1.2.3`.
 *
 * Resolved once and remembered: it cannot change while the process is alive,
 * and this is read on every request that carries the version header.
 */
export function appVersion(): string {
  if (cached) return cached;

  try {
    // Required rather than imported so a build without the native module
    // fails here, in a guarded call, rather than at module load.
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const DeviceInfo = require('react-native-device-info');
    const read = DeviceInfo?.getVersion ?? DeviceInfo?.default?.getVersion;
    const version = typeof read === 'function' ? read() : null;

    if (typeof version === 'string' && version.trim()) {
      cached = version.trim();
      return cached;
    }
  } catch {
    /*
     * Silent. This is expected under Jest and on any build predating the
     * dependency, and a warning on every launch of an app whose update gate
     * nobody is looking at is noise. The fallback below is the answer.
     */
  }

  cached = FALLBACK_VERSION;
  return cached;
}

/** Test seam - forces the next `appVersion()` to resolve again. */
export function resetAppVersionCache(): void {
  cached = null;
}
